import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { setupAxiosInterceptors } from './utils/axiosInterceptor';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PackagesPage from './pages/PackagesPage';
import PackageDetailPage from './pages/PackageDetailPage';
import PurchasePage from './pages/PurchasePage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import DestinationsPage from './pages/DestinationsPage';
import DestinationDetailPage from './pages/DestinationDetailPage';
import ResortDetailPage from './pages/ResortDetailPage';
import ContactPage from './pages/ContactPage';
import PaymentPage from './pages/PaymentPage';
import ThankYouPage from './pages/ThankYouPage';
import BookingLookupPage from './pages/BookingLookupPage';
import BookNowPage from './pages/BookNowPage';
import PayNowPage from './pages/PayNowPage';
import EnquiryThankYouPage from './pages/EnquiryThankYouPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminDestinations from './pages/admin/AdminDestinations';
import AdminResorts from './pages/admin/AdminResorts';
import AdminPackages from './pages/admin/AdminPackages';
import AdminBookings from './pages/admin/AdminBookings';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSettings from './pages/admin/Settings';
import AdminEnquiries from './pages/admin/AdminEnquiries';
import AdminLastMinuteDeals from './pages/admin/AdminLastMinuteDeals';
import CreateResort from './pages/admin/CreateResort';
import CreatePackage from './pages/admin/CreatePackage';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

function App() {
  // Initialize axios interceptors on app start
  useEffect(() => {
    setupAxiosInterceptors();
    console.log('⚙️ Axios interceptors initialized');
  }, []);

  return (
    <AuthProvider>
      <CurrencyProvider>
        <div className="App">
          <ScrollToTop />
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requireAuth>
                  <AdminLayout>
                    <Routes>
                      <Route path="/" element={<AdminDashboard />} />
                      <Route path="/dashboard" element={<AdminDashboard />} />
                      <Route path="/destinations" element={<AdminDestinations />} />
                      <Route path="/resorts" element={<AdminResorts />} />
                      <Route path="/resorts/create" element={<CreateResort />} />
                      <Route path="/resorts/edit/:id" element={<CreateResort />} />
                      <Route path="/packages" element={<AdminPackages />} />
                      <Route path="/packages/create" element={<CreatePackage />} />
                      <Route path="/packages/edit/:id" element={<CreatePackage />} />
                      <Route path="/bookings" element={<AdminBookings />} />
                      <Route path="/users" element={<AdminUsers />} />
                      <Route path="/analytics" element={<AdminAnalytics />} />
                      <Route path="/enquiries" element={<AdminEnquiries />} />
                      <Route path="/last-minute-deals" element={<AdminLastMinuteDeals />} />
                      <Route path="/settings" element={<AdminSettings />} />
                    </Routes>
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route
              path="/*"
              element={
                <>
                  <Navbar />
                  <main className="min-h-screen">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/packages" element={<PackagesPage />} />
                      <Route path="/packages/:id" element={<PackageDetailPage />} />
                      <Route path="/packages/:id/purchase" element={<PurchasePage />} />
                      {/* Existing booking lookup - user enters booking code */}
                      <Route path="/booking-confirmation/:bookingCode" element={<BookingConfirmationPage />} />
                      {/* Payment redirect - query parameters from Flywire */}
                      <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
                      <Route path="/destinations" element={<DestinationsPage />} />
                      <Route path="/destinations/:id" element={<DestinationDetailPage />} />
                      <Route path="/resorts/:slug" element={<ResortDetailPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/payment/:packageId" element={<PaymentPage />} />
                      <Route path="/book-now" element={<BookNowPage />} />
                      <Route path="/pay-now" element={<PayNowPage />} />
                      <Route path="/enquiry-thank-you" element={<EnquiryThankYouPage />} />
                      <Route path="/payment-success" element={<PaymentSuccessPage />} />
                      <Route path="/payment-failure" element={<PaymentFailurePage />} />
                      <Route path="/thank-you" element={<ThankYouPage />} />
                      <Route path="/booking-lookup" element={<BookingLookupPage />} />
                      <Route path="*" element={<div className="section-padding container-max text-center"><h1 className="text-4xl font-bold text-gray-900">404 - Page Not Found</h1></div>} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              }
            />
          </Routes>
        </div>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
